import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Nav from "@/components/Nav";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle, MailCheck } from "lucide-react";

export default function Verify() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState("pending"); // pending | success | already | error
  const [error, setError] = useState("");
  const { user, refresh } = useAuth();

  useEffect(() => {
    if (!token) {
      setState("error");
      setError("Missing verification token.");
      return;
    }
    (async () => {
      try {
        const { data } = await api.post("/auth/verify", { token });
        setState(data.already_verified ? "already" : "success");
        if (user) refresh();
      } catch (e) {
        setState("error");
        setError(formatApiError(e.response?.data?.detail) || "Verification failed");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="min-h-screen">
      <Nav />
      <main className="max-w-xl mx-auto px-6 py-24 text-center">
        {state === "pending" && (
          <>
            <div className="w-16 h-16 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center mx-auto">
              <Loader2 className="w-7 h-7 text-primary animate-spin" />
            </div>
            <h1 className="mt-6 text-3xl font-medium tracking-tight">Confirming your email…</h1>
          </>
        )}
        {(state === "success" || state === "already") && (
          <>
            <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-8 h-8 text-emerald-400" />
            </div>
            <h1 className="mt-6 text-3xl font-medium tracking-tight" data-testid="verify-success-title">
              {state === "already" ? "Already verified" : "Email confirmed"}
            </h1>
            <p className="mt-3 text-muted-foreground">
              You're ready to generate lead lists.
            </p>
            <Button asChild className="mt-8 rounded-full bg-primary hover:bg-[#FF7043] text-white" data-testid="verify-success-cta">
              <Link to={user ? "/dashboard" : "/login"}>{user ? "Go to dashboard" : "Log in"}</Link>
            </Button>
          </>
        )}
        {state === "error" && (
          <>
            <div className="w-16 h-16 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center mx-auto">
              <XCircle className="w-8 h-8 text-red-400" />
            </div>
            <h1 className="mt-6 text-3xl font-medium tracking-tight" data-testid="verify-error-title">Can't verify this link</h1>
            <p className="mt-3 text-muted-foreground">{error}</p>
            {user && (
              <Button asChild className="mt-8 rounded-full bg-primary hover:bg-[#FF7043] text-white">
                <Link to="/dashboard"><MailCheck className="w-4 h-4 mr-2" /> Request a new link</Link>
              </Button>
            )}
          </>
        )}
      </main>
    </div>
  );
}
